import { EventEmitter } from 'events';
import { OdspErrorType } from '@fluidframework/odsp-doclib-utils';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { ICriticalContainerError, ContainerErrorType, ContainerWarning } from '@fluidframework/container-definitions';
import {
  MessageBannerProvider,
  BannerType,
  BannerInfo,
  ExternalContainer,
  OWHErrorType,
  OfficeFluidErrorType,
  ITelemetryNamespaceLogger
} from '@fluidx/office-fluid-types';
import { UserActivityTracker, Locale, isMobile, sendTelemetryEvent, NamespaceLoggerAdapter } from '@fluidx/utilities';
import { useFFXHostUxIcon } from '@fluidx/icons';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { ActionDebouncer } from '../../../utilities/ActionDebouncer';
import { HostUxStrings } from '../../../stringTypes/HostUxStrings';
import { GenericEvent, GenericEventSubcategories } from '../../../telemetry/GenericEvent';
import { ContainerConnectionStateTracker } from './ContainerConnectionStateTracker';
import {
  BannerErrorInfo,
  getBannerMessagingForError,
  getBannerMessagingForContainerState
} from './getBannerMessagingForError';

/**
 * A message banner provider that defines and produces banners in response to
 * PostLoad office fluid container state signals (mostly errors and warnings)
 *
 * Where this banner provider is too opinionated on things like recovery actions possible for each error handled,
 * It's recommended a host builds their own equivalent banner provider composing or extending
 * out-of-box functionality provided here.
 *
 * Alternatively, hosts can build their fully custom provider using with the underlying error handling utilities leveraged
 */
export class ContainerBannerProvider extends EventEmitter implements MessageBannerProvider {
  private connectionStateTracker: ContainerConnectionStateTracker | undefined;
  private readonlyStateTracker: ReadonlyStateTracker | undefined;
  private userActivityTracker: UserActivityTracker | undefined;
  private stringTable: HostUxStrings | undefined;
  private logger: ITelemetryNamespaceLogger | undefined;

  private hasActiveError: boolean = false;

  constructor(
    /** Loaded office fluid container */
    private readonly container: ExternalContainer,
    /** Host locale */
    private readonly locale: Locale,
    /**
     * The action handler callback for different error scenarios.
     * It's implementation should fan out to handle all expected action types below.
     *
     * Reload - Reloads container and component.   *
     * Upgrade - Upgrade version of container and component. Usually this requires a Reload   *
     * ViewFileLocation - Open file storage location. User has been prompted to delete some files in ODSP out of storage situations.
     */
    private readonly containerBannerActionHandlers: <
      ActionType extends 'Reload' | 'Upgrade' | 'ViewFileLocation',
      ErrorType extends OfficeFluidErrorType
    >(
      actionType: ActionType,
      errorType: ErrorType
    ) => void,

    logger?: ITelemetryNamespaceLogger
  ) {
    super();

    /** Apply error namespace to telemetry logger */
    this.logger = logger && new NamespaceLoggerAdapter('ErrorUX', logger);

    this.startConnectionTracker();
    this.startReadonlyStateTracker();

    this.container.on('warning', this.containerWarningHandler);
    this.container.on('closed', this.containerClosedHandler);
  }

  public get MessageBannerProvider() {
    return this;
  }

  public reset() {
    this.hasActiveError = false;
    this.removeAllListeners();
  }

  public dispose() {
    this.reset();
    this.container.removeListener('warning', this.containerWarningHandler);
    this.container.removeListener('closed', this.containerClosedHandler);
    this.userActivityTracker?.dispose();
    this.connectionStateTracker?.dispose();
    this.readonlyStateTracker?.dispose();
  }

  private containerWarningHandler = async (warning: ContainerWarning) => {
    const bannerWarningInfo = await getBannerMessagingForError(warning, this.locale);

    // We only show banners for container throttling
    // TODO:Task 4623211: Define messaging experience for other Container warnings
    if (bannerWarningInfo.errorType === ContainerErrorType.throttlingError) {
      this.emit('CreateBanner', await this.selectBannerDefinition(bannerWarningInfo));
    }
  };

  private containerClosedHandler = async (error?: ICriticalContainerError) => {
    /**
     * Container may close for a variety of reasons, including the fact that a host may choose to close it
     * Only react to container close event with an error dialog if there's an error associated with the event
     */
    if (error) {
      const bannerErrorInfo = await getBannerMessagingForError(error, this.locale);

      this.emit('CreateBanner', await this.selectBannerDefinition(bannerErrorInfo));
      this.hasActiveError = true;
    }
  };

  private startConnectionTracker = () => {
    const disconnectedBannerType = 'Disconnected';

    this.connectionStateTracker = new ContainerConnectionStateTracker(
      this.container,
      async (newState: 'connected' | 'disconnected') => {
        if (newState === 'connected') {
          this.emit('RemoveBanner', disconnectedBannerType);
        } else if (newState === 'disconnected') {
          const bannerStrings = await getBannerMessagingForContainerState(disconnectedBannerType, this.locale);

          this.emit('CreateBanner', {
            bannerId: disconnectedBannerType,
            bannerType: BannerType.warning,
            bannerStrings,
            icon: useFFXHostUxIcon('FFXInfo')
          });

          this.logger &&
            sendTelemetryEvent(this.logger, {
              subCategory: GenericEventSubcategories.MessageBanner,
              eventName: GenericEvent.DisconnectedBannerShown
            });
        }
      }
    );

    // How long have we seen no activity from the user before we treat them as idle/inactive. Set to 1 hour.
    const userInactiveAfterMs = 60 * 60 * 1000;

    this.userActivityTracker = new UserActivityTracker(userInactiveAfterMs, this.onUserActivityStateChange);
  };

  private onUserActivityStateChange = (isActive: boolean) => {
    // If the user becomes idle, we setAutoReconnect(false) so that we do not auto-reconnect the container on disconnection. This is done to save COGS on the server while the user is idle.
    // If the user becomes active, we set autoReconnect to true and try to reconnect container if it is disconnected.
    // TODO: Move reconnect logic lower in the stack based on generic browser idle properties
    if (!this.container.closed) {
      this.container.setAutoReconnect(isActive);
      if (isActive && !this.container.connected) {
        // Since we were not trying to establish a connection while the user was inactive, let's reset the connection state tracker to
        // give the app a chance to try an establish a connection.

        // TODO: Task 4902117: Reset provider state on banner dismiss
        this.emit('RemoveBanner', 'Disconnected');
        this.connectionStateTracker?.reset();
      }
    }
  };

  private startReadonlyStateTracker = () => {
    const readonlyState = isMobile() ? 'ReadonlyMobile' : 'Readonly';

    this.readonlyStateTracker = new ReadonlyStateTracker(
      this.container,
      async () => {
        if (!this.hasActiveError) {
          const bannerStrings = await getBannerMessagingForContainerState(readonlyState, this.locale);

          this.emit('CreateBanner', {
            bannerId: readonlyState,
            bannerType: BannerType.warning,
            bannerStrings,
            icon: useFFXHostUxIcon('FFXInfo')
          });
        }
      },
      () => {
        this.emit('RemoveBanner', readonlyState);
      }
    );
  };

  private selectBannerDefinition = async ({ errorType, bannerStrings }: BannerErrorInfo): Promise<BannerInfo> => {
    if (!this.stringTable) {
      this.stringTable = await getStringTable(this.locale);
    }
    switch (errorType) {
      case OWHErrorType.ContainerUpgradeRequired:
        return {
          bannerId: errorType,
          bannerType: BannerType.warning,
          bannerStrings,
          icon: useFFXHostUxIcon('FFXError'),
          actions: [
            {
              // Upgrade
              label: this.stringTable.upgradeButtonText,
              actionCallback: () => {
                this.containerBannerActionHandlers('Upgrade', errorType);
                // TODO: Task 4902117: Reset provider state on banner dismiss
              }
            }
          ]
        };

      case DriverErrorType.fileNotFoundOrAccessDeniedError:
      case DriverErrorType.authorizationError:
        return {
          bannerId: errorType,
          bannerType: BannerType.error,
          bannerStrings,
          icon: useFFXHostUxIcon('FFXErrorBadge')
          // TODO: Task 4902117: Reset provider state on banner dismiss
        };

      case OdspErrorType.epochVersionMismatch:
        return {
          bannerId: errorType,
          bannerType: BannerType.warning,
          bannerStrings,
          icon: useFFXHostUxIcon('FFXErrorBadge'),
          actions: [
            {
              // Reload
              label: this.stringTable.reloadButtonText,
              actionCallback: () => {
                // TODO: Task 4902117: Reset provider state on banner dismiss
                this.containerBannerActionHandlers('Reload', errorType);
              }
            }
          ]
        };

      case OdspErrorType.outOfStorageError:
        return {
          bannerId: errorType,
          bannerType: BannerType.error,
          bannerStrings,
          icon: useFFXHostUxIcon('FFXErrorBadge'),
          actions: [
            {
              // View File Location
              label: this.stringTable.viewFileLocationButtonText,
              actionCallback: () => {
                this.containerBannerActionHandlers('ViewFileLocation', errorType);
                this.hasActiveError = false;
              }
            }
          ]
        };

      case DriverErrorType.throttlingError:
      case ContainerErrorType.throttlingError:
      case DriverErrorType.genericError:
      case ContainerErrorType.genericError:
      default:
        return {
          bannerId: errorType,
          bannerType: BannerType.error,
          bannerStrings,
          icon: useFFXHostUxIcon('FFXErrorBadge'),
          actions: [
            {
              // Reload Action
              label: this.stringTable.reloadButtonText,
              actionCallback: () => {
                this.containerBannerActionHandlers('Reload', errorType);
                this.hasActiveError = false;
              }
            }
          ]
        };
    }
  };
}

/**
 * This state tracker uses an action debouncer to aggregate readonly and error states when creating readonly banners.
 * On a banner signal, create readonly banner action is deferred (debounced) for a known period of time to allow for
 * any 'just-received', or 'just-to-be-received' error signals to take priority in messaging
 */
class ReadonlyStateTracker {
  /**
   * How long we should wait once we receive readonly signal before emitting.
   * This wait time allows for any accompanying error signals to be messaged with higher priority,
   */
  private debounceReadonlyNotificationMs = 100;

  private readonly createReadonlyDebouncer: ActionDebouncer;

  constructor(
    private container: ExternalContainer,
    private createReadonlyBanner: () => Promise<void>,
    private removeReadonlyBanner: () => void
  ) {
    this.createReadonlyDebouncer = new ActionDebouncer(this.createReadonlyBanner, this.debounceReadonlyNotificationMs);
    this.container.addListener('readonly', this.containerReadonlyHandler);
  }

  private containerReadonlyHandler = (readonly: boolean) => {
    if (readonly) {
      this.createReadonlyDebouncer.startOrRefreshDebounce();
    } else {
      this.createReadonlyDebouncer.cancelPendingDebounce();
      this.removeReadonlyBanner();
    }
  };

  public dispose() {
    this.container.removeListener('readonly', this.containerReadonlyHandler);
    this.createReadonlyDebouncer.dispose();
  }
}

// cspell:ignore Debouncer
